import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import * as attachmentsApi from '@/api/attachments'
import * as settingsApi from '@/api/settings'
import { useAuth } from '@/auth/AuthContext'
import { SettingsShell } from '@/components/SettingsShell'
import { getRememberedSettingsCustomer } from '@/settingsContext'
import { useSiteSettings } from '@/siteSettingsContext'

function formatBytes(n) {
  const bytes = Number(n) || 0
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

export function AttachmentsSettings() {
  const { isAdmin } = useAuth()
  const { applySettings, refresh } = useSiteSettings()
  const [customerId] = useState(() => getRememberedSettingsCustomer())
  const [extensions, setExtensions] = useState('')
  const [maxMb, setMaxMb] = useState('10')
  const [usage, setUsage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!isAdmin) return undefined
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        const [settings, usageData] = await Promise.all([
          settingsApi.getSiteSettings(),
          attachmentsApi.getAttachmentUsage(),
        ])
        if (!cancelled) {
          setExtensions(settings.attachment_allowed_extensions || '')
          setMaxMb(
            String(
              Math.max(
                1,
                Math.round((settings.attachment_max_bytes || 10485760) / (1024 * 1024)),
              ),
            ),
          )
          setUsage(usageData)
          applySettings(settings)
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            typeof err.response?.data?.detail === 'string'
              ? err.response.data.detail
              : 'Could not load attachment settings.',
          )
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [isAdmin, applySettings])

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setSaving(true)
    setError('')
    setSaved(false)
    const mb = Number(maxMb)
    if (!Number.isFinite(mb) || mb < 1) {
      setError('Maximum file size must be at least 1 MB.')
      setSaving(false)
      return
    }
    try {
      const updated = await settingsApi.updateSiteSettings({
        attachment_allowed_extensions: extensions,
        attachment_max_bytes: Math.round(mb * 1024 * 1024),
      })
      applySettings(updated)
      await refresh()
      setExtensions(updated.attachment_allowed_extensions || '')
      setMaxMb(
        String(
          Math.max(
            1,
            Math.round((updated.attachment_max_bytes || 10485760) / (1024 * 1024)),
          ),
        ),
      )
      const usageData = await attachmentsApi.getAttachmentUsage()
      setUsage(usageData)
      setSaved(true)
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not save attachment settings.',
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsShell title="Attachments" customerId={customerId || undefined}>
      <div className="space-y-6">
        <div className="rounded-xl border border-slate-200 bg-white/90 px-8 py-8 shadow-sm">
          <h1 className="font-display text-4xl tracking-tight text-brand">
            Attachments
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-brand-muted">
            Control which file types can be uploaded on category pages, the
            maximum size per file, and review how much storage is in use.
          </p>
        </div>

        {error && (
          <p className="text-sm text-red-700" role="alert">
            {error}
          </p>
        )}
        {saved && (
          <p className="text-sm text-emerald-700" role="status">
            Attachment settings saved.
          </p>
        )}

        {loading ? (
          <p className="text-brand-muted">Loading…</p>
        ) : (
          <>
            <form
              onSubmit={handleSubmit}
              className="space-y-4 rounded-xl border border-slate-200 bg-white/90 p-6 shadow-sm sm:p-8"
            >
              <label className="block text-sm">
                <span className="font-medium text-brand">Allowed extensions</span>
                <span className="mt-0.5 block text-xs text-brand-muted">
                  Comma-separated, without dots (e.g. pdf,docx,png).
                </span>
                <input
                  value={extensions}
                  onChange={(e) => setExtensions(e.target.value)}
                  className="mt-1 w-full max-w-2xl rounded border border-slate-300 px-3 py-2"
                />
              </label>
              <label className="block max-w-xs text-sm">
                <span className="font-medium text-brand">
                  Maximum file size (MB)
                </span>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={maxMb}
                  onChange={(e) => setMaxMb(e.target.value)}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                />
              </label>
              <button
                type="submit"
                disabled={saving}
                className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save settings'}
              </button>
            </form>

            <div className="rounded-xl border border-slate-200 bg-white/90 p-6 shadow-sm sm:p-8">
              <h2 className="text-lg font-semibold text-brand">Storage usage</h2>
              <p className="mt-1 text-sm text-brand-muted">
                {usage?.file_count ?? 0} file
                {(usage?.file_count ?? 0) === 1 ? '' : 's'} ·{' '}
                {formatBytes(usage?.total_bytes)} site-wide
              </p>
              <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-brand-muted">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Customer</th>
                      <th className="px-3 py-2 font-semibold">Files</th>
                      <th className="px-3 py-2 font-semibold">Space used</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {(usage?.by_customer || []).length === 0 ? (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-3 py-6 text-center text-brand-muted"
                        >
                          No attachments uploaded yet.
                        </td>
                      </tr>
                    ) : (
                      usage.by_customer.map((row) => (
                        <tr key={row.customer_id}>
                          <td className="px-3 py-2 font-medium text-brand">
                            {row.customer_name}
                          </td>
                          <td className="px-3 py-2 text-brand">{row.file_count}</td>
                          <td className="px-3 py-2 text-brand">
                            {formatBytes(row.total_bytes)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </SettingsShell>
  )
}
